/**
 * Exec Viewer Dashboard — read-only, board-pack-ready snapshot.
 *
 * Design intent: this persona is a CFO / Chief-of-Staff / Board-Observer
 * who needs the headline numbers + signed-artefact provenance but MUST
 * NOT be able to mutate anything. We deliberately:
 *   - omit the time-travel pill (no "what-if" scenarios for this seat)
 *   - omit Top Burners + Department drill-downs (operational, not exec)
 *   - lead with a "Signed" provenance pill
 *   - re-use the CeoPanels signed-reports + framework + estate panels
 *   - render a quiet "Read-only" badge in the header
 */
import React from "react";
import {
  TrendingUp,
  Coins,
  ShieldCheck,
  Lock,
  FileCheck2,
} from "lucide-react";
import { DashboardDataProvider, useDashboardData } from "@/contexts/DashboardDataContext";
import { Flywheel } from "@/components/brand/Flywheel";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import {
  fmtAud,
  KpiTile,
  SectionEyebrow,
  PanelCard,
} from "./components/DashboardPrimitives";
import {
  SpendValueChart,
  CeoGovernancePanel,
  SignedReportsPanel,
  EstatePanel,
} from "./components/CeoPanels";

export default function ExecViewerDashboard() {
  return (
    <DashboardDataProvider>
      <ExecViewerInner />
    </DashboardDataProvider>
  );
}

function ExecViewerInner() {
  const {
    tenant,
    kpis,
    frameworks,
    trend12m,
    reports,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useDashboardData();

  if (loading && !tenant) return <ExecSkeleton />;
  if (error) return <ExecError message={error.message} />;

  const ytdSpend = (trend12m || []).reduce((s, m) => s + (m.spendAud || 0), 0);
  const ytdValue = (trend12m || []).reduce((s, m) => s + (m.valueAud || 0), 0);
  const netValue = ytdValue - ytdSpend;
  const signedReports = (reports || []).filter((r) => r.signed).length;
  const totalReports = (reports || []).length;

  return (
    <div
      className="relative px-6 lg:px-8 py-6 anim-fade-up"
      data-testid="exec-viewer-dashboard"
    >
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.04} />
      </div>

      {/* Ceremonial header — quiet, "Read-only" stamp */}
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow">Boardroom · Executive Viewer</p>
            <div className="flex items-center gap-2 mt-1">
              <h1 className="text-display-md text-ink-primary">
                {tenant?.name}
              </h1>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-inset border border-border-strong text-[10px] font-bold tracking-[0.08em] uppercase text-ink-secondary"
                data-testid="exec-viewer-readonly-badge"
                title="This persona has read-only access. Mutations are blocked at the API."
              >
                <Lock size={10} /> Read-only
              </span>
            </div>
            <p className="font-ceremonial italic text-ink-secondary text-[17px] mt-1 max-w-2xl">
              The estate in numbers — every figure tied to a signed artefact in the audit chain.
            </p>
            <p className="text-meta text-ink-disabled mt-2">
              {tenant?.industry} · {tenant?.region} · view as of {new Date().toISOString().slice(0, 10)}
            </p>
          </div>
          <SyncedIndicator
            lastSyncedAt={lastSyncedAt}
            loading={loading}
            onRefetch={refetch}
            testId="exec-viewer-synced"
          />
        </div>
      </div>

      {/* Hero KPI strip — value · spend · governance · signed evidence */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile
          label="AI value YTD"
          value={fmtAud(ytdValue)}
          delta="+38% YoY"
          deltaTone="up"
          hint="net of spend"
          icon={TrendingUp}
          tone="low"
          testId="exec-kpi-value"
        />
        <KpiTile
          label="AI spend YTD"
          value={fmtAud(ytdSpend)}
          delta="−6% vs plan"
          deltaTone="up"
          hint={`net A$ ${(netValue / 1_000_000).toFixed(1)}M`}
          icon={Coins}
          tone="tralenex"
          testId="exec-kpi-spend"
        />
        <KpiTile
          label="Governance coverage"
          value={`${kpis.avgComplianceCoverage ?? 0}%`}
          delta={`${(frameworks || []).length} frameworks`}
          deltaTone="neutral"
          hint="weighted across all mapped controls"
          icon={ShieldCheck}
          tone="gold"
          testId="exec-kpi-governance"
        />
        <KpiTile
          label="Signed evidence"
          value={`${signedReports} / ${totalReports}`}
          delta="100% tamper-evident"
          deltaTone="up"
          hint="every report hash-chained"
          icon={FileCheck2}
          tone="opp"
          testId="exec-kpi-signed"
        />
      </div>

      {/* Body — narrower than CEO: chart + governance + signed reports + estate.
          Deliberately NO top-burners or department drill-downs (operational). */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <SpendValueChart />
          <CeoGovernancePanel />
        </div>
        <div className="flex flex-col gap-4">
          <SignedReportsPanel />
          <EstatePanel />
        </div>
      </div>

      {/* Boardroom narrative — same words as the CEO view, but framed
          for an observer ("the estate") rather than an operator ("our estate"). */}
      <div
        className="mt-6 surface-card shadow-e1 p-5 flex items-start gap-3 border-l-[3px] border-l-gold-600"
        data-testid="exec-narrative"
      >
        <FileCheck2 size={16} className="text-gold-600 mt-0.5 shrink-0" />
        <div>
          <SectionEyebrow>Quarterly read-out</SectionEyebrow>
          <p className="text-[15px] text-ink-primary leading-relaxed max-w-4xl">
            The governed AI estate delivered{" "}
            <span className="font-semibold tabular-nums">{fmtAud(ytdValue)}</span> of measured
            value year-to-date against{" "}
            <span className="font-semibold tabular-nums">{fmtAud(ytdSpend)}</span> of spend — a{" "}
            <span className="font-semibold tabular-nums">{(kpis.roiRatio ?? 0).toFixed(1)}×</span>{" "}
            ratio. Compliance coverage holds at{" "}
            <span className="font-semibold tabular-nums">{kpis.avgComplianceCoverage ?? 0}%</span>{" "}
            across {(frameworks || []).length} frameworks. All {totalReports} report packs in the
            library are cryptographically signed and chained to the platform&apos;s tamper-evident
            ledger.
          </p>
        </div>
      </div>

      {/* Provenance panel — what makes this view trustworthy */}
      <PanelCard
        title="Why these numbers are trustworthy"
        testId="exec-viewer-provenance"
        className="mt-4"
      >
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-3 -my-1">
          <ProvenancePoint
            icon={Lock}
            title="Read-only by design"
            body="This seat cannot promote, approve, or amend any artefact. RBAC is enforced server-side; the UI is just one layer of defence."
          />
          <ProvenancePoint
            icon={ShieldCheck}
            title="Every artefact signed"
            body="100% of report packs in the library carry a SHA-256 fingerprint chained to the platform&apos;s audit ledger. A regulator can re-verify offline."
          />
          <ProvenancePoint
            icon={FileCheck2}
            title="Same source as CAIO"
            body="No alternate dataset for the board. KPIs and reports here are the identical underlying values your CAIO is operating on right now."
          />
        </ul>
      </PanelCard>
    </div>
  );
}

function ProvenancePoint({ icon: Icon, title, body }) {
  return (
    <li className="surface-card-inner p-3 border border-border-subtle rounded-md">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-gold-600" />
        <p className="text-sm font-bold text-ink-primary">{title}</p>
      </div>
      <p className="text-meta text-ink-secondary mt-1 leading-snug">{body}</p>
    </li>
  );
}

function ExecSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="exec-viewer-loading">
      <div className="h-8 w-80 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[320px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function ExecError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="exec-viewer-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Boardroom · Executive Viewer</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load the boardroom view</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}
